import { Box, Skeleton, Typography } from "@mui/material";
import { Suspense, lazy } from "react";

import { DocumentPreview, type DocumentPreviewProps } from "./DocumentPreview";
import { UnsupportedPreview } from "./UnsupportedPreview";

const LazySpreadsheetPreview = lazy(() =>
  import("./SpreadsheetPreview").then((m) => ({
    default: m.SpreadsheetPreview,
  }))
);

const LazyDocxPreview = lazy(() =>
  import("./DocxPreview").then((m) => ({ default: m.DocxPreview }))
);

const LazyImagePreview = lazy(() =>
  import("./ImagePreview").then((m) => ({ default: m.ImagePreview }))
);

const DOCUMENT_EXTENSIONS = ["pdf", "xlsx", "csv", "docx"] as const;

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"] as const;

const ALL_PREVIEW_EXTENSIONS = [...DOCUMENT_EXTENSIONS, ...IMAGE_EXTENSIONS] as const;

type PreviewExtension = (typeof ALL_PREVIEW_EXTENSIONS)[number];

export function extensionFromName(name?: string | null): string | undefined {
  if (!name) return undefined;
  return name.split(".").pop()?.toLowerCase() || undefined;
}

export function isPreviewable(ext?: string): boolean {
  if (!ext) return false;
  return ALL_PREVIEW_EXTENSIONS.includes(ext.toLowerCase() as PreviewExtension);
}

function isImageExtension(ext: string): boolean {
  return (IMAGE_EXTENSIONS as readonly string[]).includes(ext);
}

interface FilePreviewProps extends Omit<DocumentPreviewProps, "fileUrl"> {
  fileUrl: string;
  fileExtension?: string;
}

function PreviewLoadingSkeleton() {
  return (
    <Skeleton
      variant="rectangular"
      animation="wave"
      height="100%"
      width="100%"
      sx={{ borderRadius: 0.5 }}
    />
  );
}

export function FilePreview({ fileUrl, fileExtension, ...rest }: FilePreviewProps) {
  const ext = fileExtension?.toLowerCase();

  switch (ext) {
    case "pdf":
      return <DocumentPreview fileUrl={fileUrl} {...rest} />;

    case "xlsx":
    case "csv":
      return (
        <Suspense fallback={<PreviewLoadingSkeleton />}>
          <LazySpreadsheetPreview fileUrl={fileUrl} fileExtension={ext} />
        </Suspense>
      );

    case "docx":
      return (
        <Suspense fallback={<PreviewLoadingSkeleton />}>
          <LazyDocxPreview fileUrl={fileUrl} />
        </Suspense>
      );

    case "doc":
    case "xls":
      return <UnsupportedPreview fileUrl={fileUrl} fileExtension={ext} />;

    default:
      if (ext && isImageExtension(ext)) {
        return (
          <Suspense fallback={<PreviewLoadingSkeleton />}>
            <LazyImagePreview fileUrl={fileUrl} />
          </Suspense>
        );
      }
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No preview available
          </Typography>
        </Box>
      );
  }
}
